import { useMemo, useState, useRef, memo } from 'react';
import { CheckSquare, CheckCircle2, Circle, AlertCircle, ChevronDown, ChevronRight, AlertTriangle, ArrowRight, Bell, Mail, Hash, ExternalLink } from 'lucide-react';
import { getTodayString } from '../../utils/dateUtils';
import { getBlockColor, getTaskTintBackground } from '../../utils/timeBlockColors';
import { useDrag } from '../../contexts/DragContext';

function isOverdue(task) {
  if (!task.scheduledDate || task.completedAt) return false;
  const today = getTodayString();
  return task.scheduledDate < today;
}

/**
 * TaskItem - Extracted as a standalone component to prevent re-mounting on parent re-renders.
 * This fixes the drag-and-drop issue where refs were being reset.
 */
const TaskItem = memo(function TaskItem({
  task,
  isExpanded,
  onToggleExpand,
  onToggleComplete,
  setDraggedTask
}) {
  const overdue = isOverdue(task);
  const isComplete = !!task.completedAt;

  // Get timeblock color for tinted background
  const blockColor = task.gcalTimeBlockName ? getBlockColor(task.gcalTimeBlockName) : null;
  const tintBackground = blockColor && !isComplete ? getTaskTintBackground(blockColor) : null;

  // Refs persist correctly now that component is at module level
  const isDraggingRef = useRef(false);

  const handleDragStart = (e) => {
    if (isComplete) {
      e.preventDefault();
      return;
    }
    isDraggingRef.current = true;
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedTask(task);
    e.target.classList.add('opacity-50', 'scale-95');
  };

  const handleDragEnd = (e) => {
    isDraggingRef.current = false;
    setDraggedTask(null);
    e.target.classList.remove('opacity-50', 'scale-95');
  };

  const handleClick = (e) => {
    // Don't expand if we were dragging
    if (isDraggingRef.current) {
      return;
    }
    onToggleExpand(task.id);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleComplete(task, e);
  };

  return (
    <div
      draggable={!isComplete}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`rounded-lg border transition-all relative
        ${overdue && !isComplete ? 'border-red-500/30 bg-red-500/5' : 'border-border/50'}
        ${isComplete ? 'opacity-50 bg-surface-card' : 'hover:bg-surface-hover'}
        ${!isComplete ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{
        backgroundColor: tintBackground || (overdue && !isComplete ? undefined : 'var(--surface-card)'),
        borderLeftColor: blockColor || undefined,
        borderLeftWidth: blockColor ? '3px' : undefined,
      }}
    >
      <div
        className="flex items-center gap-2 p-2.5"
        onClick={handleClick}
      >
        {/* Checkbox */}
        <button
          onClick={handleCheckboxClick}
          className="flex-shrink-0 bg-transparent border-none cursor-pointer p-0"
        >
          {isComplete ? (
            <CheckCircle2 size={18} className="text-green-500" />
          ) : (
            <Circle size={18} className="text-text-muted hover:text-accent transition-colors" />
          )}
        </button>

        {/* Task info */}
        <div className="flex-1 min-w-0">
          <span className={`text-sm block truncate ${isComplete ? 'line-through text-text-muted' : 'text-text-primary'}`}>
            {task.title}
          </span>

          {/* Badge row - context, overdue, delegation, follow-up, source links */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {/* 1. Context badge */}
            {task.context && (
              <span className="text-[10px] text-text-tertiary">
                {task.context}
              </span>
            )}

            {/* 2. Overdue icon (icon-only to save space) */}
            {overdue && !isComplete && (
              <AlertTriangle size={14} className="text-red-500" />
            )}

            {/* 3. Delegated to badge */}
            {(task.delegatedTo || task.delegatedToName) && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 flex items-center gap-0.5">
                <ArrowRight size={10} />
                {task.delegatedToName || task.delegatedTo}
              </span>
            )}

            {/* 4. Follow-up badge (hidden if delegatedTo is shown - it's implied) */}
            {task.followUpDate && !(task.delegatedTo || task.delegatedToName) && (() => {
              const today = getTodayString();
              const isFollowUpOverdue = task.followUpDate < today;
              const isFollowUpToday = task.followUpDate === today;
              return (
                <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                  isFollowUpOverdue ? 'bg-red-500/15 text-red-400' :
                  isFollowUpToday ? 'bg-amber-500/15 text-amber-400' :
                  'bg-surface-hover text-text-tertiary'
                }`}>
                  <Bell size={10} />
                  {isFollowUpOverdue ? 'Follow up overdue' : isFollowUpToday ? 'Follow up today' : `Follow up ${task.followUpDate}`}
                </span>
              );
            })()}

            {/* 5. Email link */}
            {task.source === 'email' && task.sourceLink && (
              <a
                href={task.sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 flex items-center gap-0.5"
              >
                <Mail size={10} />
                Email
                <ExternalLink size={8} />
              </a>
            )}

            {/* 6. Slack link */}
            {task.source === 'slack' && task.sourceLink && (
              <a
                href={task.sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 flex items-center gap-0.5"
              >
                <Hash size={10} />
                {task.sourceChannel || 'Slack'}
                <ExternalLink size={8} />
              </a>
            )}
          </div>
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-1">
          {overdue && !isComplete && (
            <AlertCircle size={14} className="text-red-500" />
          )}
          {task.notes && (
            isExpanded ? (
              <ChevronDown size={14} className="text-text-tertiary" />
            ) : (
              <ChevronRight size={14} className="text-text-tertiary" />
            )
          )}
        </div>
      </div>

      {/* Expanded notes */}
      {isExpanded && task.notes && (
        <div className="px-2.5 pb-2.5 pt-0">
          <div className="text-xs text-text-secondary bg-surface-elevated p-2 rounded border-l-2 border-accent">
            {task.notes}
          </div>
        </div>
      )}
    </div>
  );
});

export default function DashboardTasksColumn({
  tasks,
  timeBlocks,
  onComplete,
  onUncomplete
}) {
  const [expandedTask, setExpandedTask] = useState(null);
  const { setDraggedTask } = useDrag();

  // Separate and sort tasks, group scheduled by timeblock
  const { tasksByBlock, unscheduledTasks, completedTasks } = useMemo(() => {
    const todayStr = getTodayString();
    const byBlock = {}; // { blockName: { block, tasks: [] } }
    const unscheduled = [];
    const completed = [];

    tasks.forEach(task => {
      if (task.completedAt) {
        // Only show in "Completed" section if completed TODAY (local timezone)
        const completedTimestamp = task.completedAt.toDate
          ? task.completedAt.toDate()
          : new Date(task.completedAt);
        const completedDate = completedTimestamp.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        if (completedDate === todayStr) {
          completed.push(task);
        }
        // Skip tasks completed on other days - don't show in dashboard
      } else if (task.gcalTimeBlockId) {
        const block = timeBlocks.find(tb => tb.id === task.gcalTimeBlockId);
        const blockName = task.gcalTimeBlockName || block?.summary || 'Scheduled';

        if (!byBlock[blockName]) {
          byBlock[blockName] = { block, tasks: [] };
        }
        byBlock[blockName].tasks.push({ ...task, block });
      } else {
        unscheduled.push(task);
      }
    });

    // Convert to array and sort by block start time
    const tasksByBlock = Object.entries(byBlock)
      .map(([name, data]) => ({
        name,
        block: data.block,
        tasks: data.tasks
      }))
      .sort((a, b) => {
        if (a.block && b.block) {
          const aStart = new Date(a.block.start?.dateTime || a.block.start);
          const bStart = new Date(b.block.start?.dateTime || b.block.start);
          return aStart - bStart;
        }
        if (a.block) return -1;
        if (b.block) return 1;
        return 0;
      });

    return { tasksByBlock, unscheduledTasks: unscheduled, completedTasks: completed };
  }, [tasks, timeBlocks]);

  // Calculate progress (only count tasks completed TODAY)
  const progress = useMemo(() => {
    const todayStr = getTodayString();
    // Total = incomplete tasks + tasks completed today
    const incompleteTasks = tasks.filter(t => !t.completedAt);
    const tasksCompletedToday = tasks.filter(t => {
      if (!t.completedAt) return false;
      const completedTimestamp = t.completedAt.toDate
        ? t.completedAt.toDate()
        : new Date(t.completedAt);
      const completedDate = completedTimestamp.toLocaleDateString('en-CA');
      return completedDate === todayStr;
    });
    const total = incompleteTasks.length + tasksCompletedToday.length;
    const done = tasksCompletedToday.length;
    return { done, total };
  }, [tasks]);

  const handleToggleComplete = (task, e) => {
    e.stopPropagation();
    if (task.completedAt) {
      onUncomplete(task.id);
    } else {
      onComplete(task.id);
    }
  };

  const handleToggleExpand = (taskId) => {
    setExpandedTask(prev => prev === taskId ? null : taskId);
  };

  return (
    <div className="dashboard-column">
      {/* Header */}
      <div className="dashboard-column-header">
        <div className="flex items-center gap-2">
          <CheckSquare size={18} className="text-weekly" />
          <h2 className="text-sm font-semibold text-text-primary">Today's Tasks</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">
            {progress.done}/{progress.total}
          </span>
          {progress.total > 0 && (
            <div className="w-16 h-1.5 bg-surface-hover rounded-full overflow-hidden">
              <div
                className="h-full bg-weekly rounded-full transition-all duration-300"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="dashboard-column-content">
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckSquare size={24} className="text-text-muted mb-2" />
            <p className="text-sm text-text-secondary">No tasks for today</p>
            <p className="text-[10px] text-text-tertiary mt-1">
              Schedule tasks or focus them for today
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Unscheduled tasks - FIRST (drag these to schedule) */}
            {unscheduledTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[10px] text-text-tertiary uppercase tracking-wide font-medium">
                    Unscheduled ({unscheduledTasks.length})
                  </span>
                </div>
                <div className="space-y-1">
                  {unscheduledTasks.map(task => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      isExpanded={expandedTask === task.id}
                      onToggleExpand={handleToggleExpand}
                      onToggleComplete={handleToggleComplete}
                      setDraggedTask={setDraggedTask}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tasks grouped by timeblock */}
            {tasksByBlock.map(({ name, block, tasks: blockTasks }) => {
              const blockColor = getBlockColor(name);
              return (
                <div key={name}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {blockColor && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: blockColor }}
                      />
                    )}
                    <span className="text-[10px] text-text-tertiary uppercase tracking-wide font-medium">
                      {name} ({blockTasks.length})
                    </span>
                  </div>
                  <div className="space-y-1">
                    {blockTasks.map(task => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        isExpanded={expandedTask === task.id}
                        onToggleExpand={handleToggleExpand}
                        onToggleComplete={handleToggleComplete}
                        setDraggedTask={setDraggedTask}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Completed tasks */}
            {completedTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CheckCircle2 size={12} className="text-green-500" />
                  <span className="text-[10px] text-text-tertiary uppercase tracking-wide font-medium">
                    Completed ({completedTasks.length})
                  </span>
                </div>
                <div className="space-y-1">
                  {completedTasks.slice(0, 3).map(task => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      isExpanded={expandedTask === task.id}
                      onToggleExpand={handleToggleExpand}
                      onToggleComplete={handleToggleComplete}
                      setDraggedTask={setDraggedTask}
                    />
                  ))}
                  {completedTasks.length > 3 && (
                    <p className="text-[10px] text-text-muted text-center py-1">
                      +{completedTasks.length - 3} more completed
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
